/**
 * LineChart — recharts-powered multi-series line chart.
 * Maintains the same external interface as the previous SVG implementation.
 */
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import type { CSSProperties } from 'react'

export interface ChartSeries {
  id:    string
  label: string
  color: string
  /** data must cover the same X domain — use null for missing values */
  data:  { x: string; y: number | null }[]
}

interface Props {
  series:   ChartSeries[]
  formatX?: (x: string) => string
  formatY?: (y: number) => string
  yUnit?:   string
  noData?:  string
}

export default function LineChart({ series, formatX, formatY, yUnit = '', noData = '—' }: Props) {
  const allX = [...new Set(series.flatMap(s => s.data.map(d => d.x)))].sort()

  if (allX.length === 0) return <p style={css.empty}>{noData}</p>

  // Build flat data array: one object per X value with each series as a key
  const data = allX.map(x => {
    const row: Record<string, string | number | null> = { x }
    for (const ser of series) {
      row[ser.id] = ser.data.find(d => d.x === x)?.y ?? null
    }
    return row
  })

  const fmtY = (v: number) => (formatY ? formatY(v) : String(v)) + yUnit

  return (
    <div style={css.wrapper}>
      <ResponsiveContainer width="100%" height={220}>
        <ReLineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ede9e3" />
          <XAxis
            dataKey="x"
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: '#aaa' }}
            tickLine={false}
            axisLine={{ stroke: '#d8d0c8' }}
          />
          <YAxis
            tickFormatter={fmtY}
            tick={{ fontSize: 11, fill: '#aaa' }}
            tickLine={false}
            axisLine={false}
            width={48}
          />
          <Tooltip
            formatter={(value, name) => {
              const v = typeof value === 'number' ? fmtY(value) : String(value)
              const ser = series.find(s => s.id === String(name))
              return [v, ser?.label ?? String(name)]
            }}
            labelFormatter={(label) => formatX ? formatX(String(label)) : String(label)}
            contentStyle={{ fontSize: 13, borderRadius: 8, border: '1px solid #e8e0d5' }}
          />
          {series.map(ser => (
            <Line
              key={ser.id}
              type="monotone"
              dataKey={ser.id}
              name={ser.id}
              stroke={ser.color}
              strokeWidth={2.5}
              dot={{ r: 4, fill: ser.color, strokeWidth: 1.5, stroke: '#fff' }}
              activeDot={{ r: 6 }}
              connectNulls={false}
            />
          ))}
        </ReLineChart>
      </ResponsiveContainer>
    </div>
  )
}

const css: Record<string, CSSProperties> = {
  wrapper: { width: '100%' },
  empty:   { textAlign: 'center', color: '#bbb', padding: '48px 0', fontSize: '1rem', margin: 0 },
}
